// A value the reader was not confident of, entered where it is on the Test (TGCLOUD-1868, plan B1,
// D3; decision 3; hazard 1868-H1). It arrives EMPTY: the reading's value is never offered, so it
// cannot be accepted with a click. Nor can what the Test held there before the report: the
// application empties that too, and it is said beside the field ("This Test held: GRCh37") to be
// chosen or typed again if the report says so. A value counts for its group only once it is
// entered here and compared with what was read (useReadingChecks).
//
//   text    typed as the report prints it ("Type it as printed"): a c., p. or g. description, a
//           transcript
//   coded   chosen from a list with NO default: a genome build, a zygosity ("Not stated" is a
//           value), a variant interpretation (with "Not stated" beside the five tiers where the
//           reading holds none of them, saved as no interpretation: a laboratory call the report
//           never made is never the only way to save it)
//   date    a calendar date picked, stored as the Test stores a calendar date (toStoredDate)
//
// Beside it, "Show in the report" where the report prints it (AnchorMark), and why it arrived
// empty. Once entered (a choice made, or a text field left), what was entered is compared with what
// was read (readingDigest's browser twin): "Matches what was read", or "Differs from what was read.
// Check the report." The clinician may keep their own value; the save compares again on the
// server.
import React from 'react';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import DateInput from 'src/components/UI/input/DateInput';
import useDateFormatter from 'src/hooks/useDateFormatter';
import { toStoredDate } from 'src/util/util';
import { toLocalDate } from 'src/util/partialDate';
import AnchorMark from './AnchorMark';
import { NOT_STATED_CHOICE, useReadingChecksContext } from './useReadingChecks';
import { NOT_STATED_FIELDS, fieldKindOf } from './verificationGroups';
import {
  DIFFERS_FROM_READING,
  LOW_FIELD_LABELS,
  MATCHES_READING,
  NOT_STATED_LABEL,
  TYPE_AS_PRINTED,
  heldBeforeText,
  lowConfidenceReasonText,
  notFilledInText
} from './labReportVerificationVocabulary';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// The parts of a variant where the report prints a value, for its "Show in the report".
const ANCHOR_FIELDS = Object.freeze({
  variantC: ['variantC', 'hgvsAsPrinted.c'],
  variantP: ['variantP', 'hgvsAsPrinted.p'],
  variantG: ['variantG', 'hgvsAsPrinted.g'],
  transcript: ['transcript', 'transcriptAsPrinted'],
  genomeBuild: ['genomeBuild'],
  zygosity: ['zygosity'],
  variantInterpretation: [
    'variantInterpretation',
    'labClassification.asPrinted'
  ],
  'labClassification.classificationDate': [
    'labClassification.classificationDate',
    'labClassification.classificationDateAsPrinted'
  ]
});

/** The parts of a variant that say where the report prints `field`. */
export const anchorFieldsOf = (field) => ANCHOR_FIELDS[field] || [field];

// What a coded value holds as its choice: a zygosity is { value }, the rest a code.
const choiceOf = (field, value) =>
  field === 'zygosity'
    ? (value && typeof value === 'object' && value.value) || ''
    : typeof value === 'string'
    ? value
    : '';
const valueOfChoice = (field, choice) =>
  field === 'zygosity' ? (choice ? { value: choice } : null) : choice || null;

/**
 * A coded field's list, with the value the field holds added where the list does not offer it: a
 * select shows nothing for a value outside its list, so a value it holds would read as empty.
 */
export function optionsWithCurrent(options, choice) {
  const list = Array.isArray(options) ? options : [];
  return choice && !list.some((o) => o.value === choice)
    ? [...list, { value: choice, label: choice }]
    : list;
}

/** What the Test held at a field, in words: a code by its label, a date as the site writes one. */
function heldText({ kind, field, held, options, fmtDate }) {
  if (held === null || held === undefined || held === '') return '';
  if (kind === 'coded') {
    const choice = choiceOf(field, held);
    return (options.find((o) => o.value === choice) || {}).label || choice;
  }
  if (kind === 'date') {
    const date = toLocalDate(held);
    return date ? fmtDate(date) : '';
  }
  return typeof held === 'string' ? held : '';
}

/** "Matches what was read" or "Differs from what was read. Check the report.", once entered. */
export function ReadingComparison({ path, value }) {
  const t = useAutoTranslation();
  const checks = useReadingChecksContext();
  const result = checks ? checks.comparisonOf(path, value) : undefined;
  if (result !== true && result !== false) return null;
  return (
    <Typography
      variant="caption"
      component="div"
      role="status"
      data-testid="reading-comparison"
      data-matches={result ? 'true' : 'false'}
      sx={{
        mt: 0.25,
        fontWeight: 600,
        color: result ? 'success.dark' : 'warning.dark'
      }}
    >
      {t(result ? MATCHES_READING : DIFFERS_FROM_READING)}
    </Typography>
  );
}

/**
 * @param {object} props
 * @param {string} props.path      the reading's path (`variants.0.genomeBuild`)
 * @param {string} props.field     the field on the variant (`genomeBuild`)
 * @param {*} props.value          what the form holds there now
 * @param {(value) => void} props.onChange
 * @param {string} props.target    the value's verification target (`variant:<findingId>`)
 * @param {string} [props.name]    the variant in words, for a screen reader
 * @param {Array<{ value, label }>} [props.options]  a coded field's list, its labels shown as they are
 *   (translated by the caller where they are the app's own words)
 * @param {string} [props.helperText]  a field's own rule, said under it (a versioned transcript)
 * @param {boolean} [props.error]
 */
export default function LowConfidenceField({
  path,
  field,
  value,
  onChange,
  target,
  name = '',
  options = [],
  helperText = null,
  error = false
}) {
  const t = useAutoTranslation();

  const checks = useReadingChecksContext();
  const fmtDate = useDateFormatter();
  if (!checks || !checks.active) return null;
  const low = checks.lowAt(path);
  if (!low) return null;
  const kind = fieldKindOf(field);
  // The field's own label in words; a c., p. or g. is HGVS notation, the same in every language.
  const fieldLabel = LOW_FIELD_LABELS[field]
    ? /^[cpg]\.$/.test(LOW_FIELD_LABELS[field])
      ? LOW_FIELD_LABELS[field]
      : t(LOW_FIELD_LABELS[field])
    : field;
  const label = name
    ? t('{{field}} of {{name}}', { field: fieldLabel, name })
    : fieldLabel;
  const held = heldText({
    kind,
    field,
    held: checks.heldBefore ? checks.heldBefore(path) : null,
    options,
    fmtDate
  });
  const empty =
    kind === 'coded'
      ? !choiceOf(field, value)
      : typeof value !== 'string' || value.trim() === '';
  const why = empty
    ? notFilledInText(lowConfidenceReasonText(low.reason, t), t)
    : null;
  const helper = [helperText, why].filter(Boolean).join(' ') || undefined;
  const inputProps = {
    'data-testid': `low-confidence-${path}`,
    'data-low-confidence': path
  };

  // "Not stated" chosen where the list has no such code: the form holds no value, and the choice is
  // the review's (useReadingChecks.chooseNotStated). Offered only where nothing was read.
  const offersNotStated =
    NOT_STATED_FIELDS.includes(field) && !low.readingDigest;
  const chosenNotStated =
    offersNotStated && !choiceOf(field, value) && checks.isNotStated(path);
  let input;
  if (kind === 'coded') {
    const choice = chosenNotStated ? NOT_STATED_CHOICE : choiceOf(field, value);
    const shown = offersNotStated
      ? [...options, { value: NOT_STATED_CHOICE, label: t(NOT_STATED_LABEL) }]
      : options;
    input = (
      <TextField
        select
        fullWidth
        size="small"
        label={label}
        value={choice}
        onChange={(e) => {
          if (offersNotStated && e.target.value === NOT_STATED_CHOICE) {
            onChange('');
            checks.chooseNotStated(path);
            return;
          }
          const next = valueOfChoice(field, e.target.value);
          onChange(next);
          checks.commit(path, next);
        }}
        helperText={chosenNotStated ? helperText || undefined : helper}
        slotProps={{ htmlInput: inputProps }}
      >
        {optionsWithCurrent(shown, choice).map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>
    );
  } else if (kind === 'date') {
    // The picker's own label names the field for a screen reader, and why it is empty is said
    // beneath it: DateInput shows its helper text only with an error (review of the view).
    input = (
      <Box>
        <DateInput
          value={toLocalDate(value)}
          onChange={(date) => {
            const next = date ? toStoredDate(date) : null;
            onChange(next);
            checks.commit(path, next);
          }}
          variant="outline"
          slotProps={{
            textField: {
              label,
              size: 'small',
              slotProps: { inputLabel: { shrink: true } }
            }
          }}
        />
        {helper && (
          <Typography
            variant="caption"
            component="div"
            data-testid="low-confidence-why"
            sx={{ color: 'text.secondary', mt: 0.25, mx: 1.75 }}
          >
            {helper}
          </Typography>
        )}
      </Box>
    );
  } else {
    const text = typeof value === 'string' ? value : '';
    input = (
      <TextField
        fullWidth
        size="small"
        label={label}
        placeholder={t(TYPE_AS_PRINTED)}
        value={text}
        error={error}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => checks.commit(path, e.target.value)}
        helperText={helper}
        slotProps={{ htmlInput: inputProps, inputLabel: { shrink: true } }}
      />
    );
  }

  return (
    <Box data-testid="low-confidence-field" data-path={path} sx={{ mt: 1 }}>
      {input}
      {held && (
        <Typography
          variant="caption"
          component="div"
          data-testid="held-before"
          sx={{ color: 'text.secondary', mt: 0.25 }}
        >
          {heldBeforeText(held, t)}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        <AnchorMark
          target={target}
          fields={anchorFieldsOf(field)}
          label={label}
          sx={{ ml: 0 }}
        />
      </Box>
      <ReadingComparison path={path} value={value} />
    </Box>
  );
}
